import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionDropdown } from '@/components';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { warehouseApi } from '../../api/warehouse.api';
import { EmptyTable, ListSearch, ListShell } from '../../components/ListShell';
import { HeaderActions } from '../../components/pickers';
import {
  codeCls,
  dateCls,
  LinesQty,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  PageHeader,
} from '../../components/ui';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { TRANSFER_FILTERS } from '../../filterFields';
import { fmtDateTime, fmtQty } from '../../format';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { WAREHOUSE_PATHS } from '../../paths';
import { TRANSFER_STATUS, TRANSFER_TABS } from '../../status';
import type { TransferSummary } from '../../types';
import { useWarehouse } from '../../WarehouseContext';

type Tab = (typeof TRANSFER_TABS)[number];

/** A bin: its own name, with the path above it when that says more. */
const BinCell: React.FC<{ name: string; path: string }> = ({ name, path }) => (
  <div className="min-w-0">
    <p className={nameCls}>{name}</p>
    {path && path !== name && <p className={`${metaCls} truncate`}>{path}</p>}
  </div>
);

const StorageTransferListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken } = useWarehouse();
  const q = useListQuery<Tab>(TRANSFER_TABS, 'ALL', TRANSFER_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;

  // The storage filter chain sends `storageID`; a transfer filters on its source bin.
  const { storageID, ...restFilters } = filterApi;
  const common = {
    hubID,
    ...restFilters,
    sourceStorageID: storageID ? String(storageID) : null,
    search,
  };

  const list = useLoad(
    (signal) =>
      warehouseApi.listTransfers(
        {
          ...common,
          status: q.tab === 'ALL' ? null : q.tab,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, filterKey, search, q.tab, q.page, q.limit, refreshToken],
    ready
  );
  // Tab counts: the list's filters (minus the tab) — never disagree with it.
  const summary = useLoad(
    (signal) => warehouseApi.getTransferSummary(common, { signal }),
    [hubID, filterKey, search, refreshToken],
    ready
  );

  const tabs = TRANSFER_TABS.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'Semua' : TRANSFER_STATUS[k].label,
    count: summary.data
      ? k === 'ALL'
        ? summary.data.total
        : (summary.data.counts[k] ?? 0)
      : null,
  }));

  const columns = useMemo<TableDataColumn<TransferSummary>[]>(
    () => [
      {
        key: 'code',
        title: 'Kode',
        render: (_v, t) => <span className={codeCls}>{t.code}</span>,
      },
      {
        key: 'source',
        title: 'Asal',
        render: (_v, t) => (
          <BinCell name={t.sourceStorageName} path={t.sourceStoragePath} />
        ),
      },
      {
        key: 'dest',
        title: 'Tujuan',
        render: (_v, t) => (
          <BinCell name={t.destStorageName} path={t.destStoragePath} />
        ),
      },
      {
        key: 'lines',
        title: 'Baris / Qty',
        align: 'right',
        render: (_v, t) => (
          <LinesQty lines={t.lineCount} qty={fmtQty(t.totalQty)} />
        ),
      },
      {
        key: 'createdBy',
        title: 'Pemohon',
        render: (_v, t) => <span className={nameCls}>{t.createdBy}</span>,
      },
      {
        key: 'createdAt',
        title: 'Tanggal',
        render: (_v, t) => (
          <span className={dateCls}>{fmtDateTime(t.createdAt)}</span>
        ),
      },
      {
        key: 'status',
        title: 'Status',
        render: (_v, t) => <MappedBadge map={TRANSFER_STATUS} value={t.status} />,
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, t) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'Lihat detail',
                icon: 'ri-eye-line',
                onClick: () => navigate(`${WAREHOUSE_PATHS.storageTransfer}/${t.id}`),
              },
            ]}
          />
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Warehouse management"
        title="Transfer storage"
        actions={
          <HeaderActions>
            <Button
              data-testid="transfer-create"
              onClick={() =>
                navigate(`${WAREHOUSE_PATHS.storageTransfer}/new`)
              }
              leftIcon={<i className="ri-add-line text-[14px]" />}
            >
              Ajukan transfer
            </Button>
          </HeaderActions>
        }
      />
      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Cari kode, storage, SKU…"
            />
            <WarehouseFilterPanel
              filters={q.filters}
              title="Filter transfer storage"
            />
          </>
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <TableData
            size="sm"
            loading={list.loading || !ready}
            data={list.data?.data ?? []}
            columns={columns}
            onRowClick={(t) =>
              navigate(`${WAREHOUSE_PATHS.storageTransfer}/${t.id}`)
            }
            emptyState={<EmptyTable title="Belum ada transfer storage" />}
          />
        )}
      </ListShell>
    </div>
  );
};

export default StorageTransferListPage;
